import { containsTextCaseInsensitive } from 'lib/utils/strings';
import {
  WORK_ORDER_OPERATION_FIELDS,
  parseSearchFields,
  isBarcodeFormattedSearch,
} from 'lib/utils/workOrderOperationSearch';

const baseWhere = {
  closedDate: { _is_null: true },
  status: { _nin: ['C', 'X', 'COMPLETED'] },
};

const getByJobWhereClause = (job) => {
  const jobSequence = job.operation;

  const conditions = [];

  if (job.name) {
    conditions.push({ partNumber: { _ilike: `%${job.name}%` } });
  }
  if (job.part) {
    conditions.push({ partNumber: { _ilike: `%${job.part}%` } });
  }

  return {
    ...baseWhere,
    ...(jobSequence ? { sequenceNumber: { _eq: jobSequence } } : {}),
    workOrder: {
      part: conditions.length > 0 ? { _or: conditions } : {},
    },
  };
};

export const getByPartsWhere = ({
  latestJob,
  isAPMEnabled,
  orderOperation,
}) => {
  const partOperationRef = orderOperation?.partOperationRef;

  if (isAPMEnabled && !partOperationRef) {
    return getByJobWhereClause(latestJob);
  }

  if (!orderOperation) {
    return baseWhere;
  }

  return {
    ...baseWhere,
    sequenceNumber: { _eq: orderOperation.sequenceNumber },
    workOrder: {
      part: {
        partOperations: {
          partOperationRef: { _eq: partOperationRef },
        },
      },
    },
  };
};

/**
 * Build GraphQL where clause from parsed work order operation fields
 *
 * @param {Object} parsedFields - Parsed field values from parseSearchFields()
 * @param {Function} trimFn - Optional trim function for values
 * @returns {Object} GraphQL where clause with _and conditions
 *
 * @example
 * buildGraphQLWhereClause({ workOrderId: "123", sequenceNumber: "10" }, trim)
 * // Returns: { _and: [{ workOrderId: { _ilike: "%123%" }}, { sequenceNumber: { _eq: 10 }}]}
 */
export const buildGraphQLWhereClause = (
  parsedFields,
  trimFn = (val) => {
    return val;
  }
) => {
  const andConditions = [];

  // workOrderId - use partial match (ILIKE)
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID]) {
    andConditions.push({
      workOrderId: {
        _ilike: `%${trimFn(
          parsedFields[WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID]
        )}%`,
      },
    });
  }

  // sequenceNumber - use exact match (numeric)
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER]) {
    const sequenceNum = Number.parseInt(
      parsedFields[WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER],
      10
    );
    if (!Number.isNaN(sequenceNum)) {
      andConditions.push({
        sequenceNumber: { _eq: sequenceNum },
      });
    }
  }

  // lot - use exact match
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.LOT]) {
    andConditions.push({
      lot: { _eq: parsedFields[WORK_ORDER_OPERATION_FIELDS.LOT] },
    });
  }

  // sub - use exact match
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.SUB]) {
    andConditions.push({
      sub: { _eq: parsedFields[WORK_ORDER_OPERATION_FIELDS.SUB] },
    });
  }

  // split - use exact match
  if (parsedFields[WORK_ORDER_OPERATION_FIELDS.SPLIT]) {
    andConditions.push({
      split: { _eq: parsedFields[WORK_ORDER_OPERATION_FIELDS.SPLIT] },
    });
  }

  // Return _and clause if we have conditions, empty object otherwise
  if (andConditions.length > 0) {
    return { _and: andConditions };
  }

  return {};
};

export const searchOnProductionScheduleOperations = (search, operations) => {
  if (!search || !operations) {
    return operations;
  }

  // Check if search has barcode field prefixes
  const isBarcodeFormat = isBarcodeFormattedSearch(search);

  return operations.filter((workOrderOperation) => {
    if (isBarcodeFormat) {
      // Parse fields and use AND-based filtering for barcode searches
      const parsedFields = parseSearchFields(search);
      let matches = true;

      // All fields must match (AND logic)
      if (parsedFields.workOrderId) {
        matches =
          matches &&
          containsTextCaseInsensitive(
            workOrderOperation?.workOrder?.workOrderId,
            parsedFields.workOrderId
          );
      }

      // Check sequenceNumber - exact match
      if (parsedFields.sequenceNumber) {
        const sequenceNum = Number.parseInt(parsedFields.sequenceNumber, 10);
        matches = matches && workOrderOperation?.sequenceNumber === sequenceNum;
      }

      // Check lot - exact match
      if (parsedFields.lot) {
        matches = matches && workOrderOperation?.lot === parsedFields.lot;
      }

      // Check sub - exact match
      if (parsedFields.sub) {
        matches = matches && workOrderOperation?.sub === parsedFields.sub;
      }

      // Check split - exact match
      if (parsedFields.split) {
        matches = matches && workOrderOperation?.split === parsedFields.split;
      }

      return matches;
    }

    // Use original OR-based text search for plain text
    return (
      containsTextCaseInsensitive(
        workOrderOperation?.workOrder?.partNumber,
        search
      ) ||
      containsTextCaseInsensitive(
        workOrderOperation?.workOrder?.workOrderId,
        search
      )
    );
  });
};

// trim whitespace from start and end of string
// and remove leading zeroes
// samples:
// ' 123 ' -> '123'
// '00123' -> '123'
// '123' -> '123'
// '000' -> ''
// '  123  ' -> '123'
// '000123000' -> '123'
export const trim = (value) => {
  try {
    return value.replace(/^[\s0]+/, '').replace(/\s+$/, '');
  } catch {
    return value;
  }
};

export const provideFallbackForCycleTimeMs = (operations) => {
  return operations.map((operation) => {
    const partOperationCycleTimeMs =
      operation.matchingPartOperations[0]?.cycleTimeMs || 0;
    return {
      ...operation,
      // if no operation cycleTimeMs, use cycleTimeMs on partOperation
      cycleTimeMs: operation.cycleTimeMs || partOperationCycleTimeMs,
    };
  });
};
